/**
 * @fileoverview Client-side error tracking utilities
 * 
 * @purpose Provides client-side error tracking functionality for React applications.
 *          Includes global error handlers and error boundary utilities.
 * 
 * @usage Import in client components:
 *        import { errorTracker, setupGlobalErrorHandling, withErrorTracking } from '@korvo/core/lib/error-tracking-client'
 * 
 * @ownership Core infrastructure - error tracking
 */

'use client'

import React from 'react'

export type ErrorContext = {
  userId?: string
  requestId?: string
  userAgent?: string
  ipAddress?: string
  url?: string
  method?: string
  stack?: string
  component?: string
  action?: string
  metadata?: Record<string, unknown>
}

class ClientErrorTracker {
  private errorCounts = new Map<string, number>()

  async trackError(
    error: Error,
    context: ErrorContext = {},
    severity?: string
  ) {
    const errorKey = this.generateErrorKey(error, context)
    const currentCount = this.errorCounts.get(errorKey) || 0
    this.errorCounts.set(errorKey, currentCount + 1)

    // Client‑side tracking avoids any Prisma / server imports.
    // In the future we can post to a lightweight logging endpoint here.
     
    console.error('Client error tracked:', {
      message: (error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error)),
      name: error.name,
      stack: error.stack,
      context,
      severity
    })
  }

  private generateErrorKey(error: Error, context: ErrorContext): string {
    const baseKey = `${error.name}:${(error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error))}`
    const contextKey = context.component ? `:${context.component}` : ''
    return `${baseKey}${contextKey}`
  }
}

export const errorTracker = new ClientErrorTracker()

export function setupGlobalErrorHandling() {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (event) => {
    const errorEvent = event as ErrorEvent;
    const errorMessage = errorEvent.error?.message ?? errorEvent.message ?? 'Unknown error';
    void errorTracker.trackError(
      new Error(errorMessage),
      {
        url: window.location.href,
        userAgent: navigator.userAgent,
        component: 'global'
      },
      'high'
    )
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    const errorMessage = reason instanceof Error ? reason.message : String(reason ?? 'Unhandled Promise Rejection');
    void errorTracker.trackError(
      new Error(errorMessage),
      {
        url: window.location.href,
        userAgent: navigator.userAgent,
        component: 'promise'
      },
      'high'
    )
  })
}

type ErrorTrackingBoundaryProps = {
  componentName: string
  metadata?: Record<string, unknown>
  children: React.ReactNode
}

type ErrorTrackingBoundaryState = {
  error?: Error
}

class ErrorTrackingBoundary extends React.Component<
  ErrorTrackingBoundaryProps,
  ErrorTrackingBoundaryState
> {
  state: ErrorTrackingBoundaryState = {}

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    void errorTracker.trackError(
      error,
      {
        component: this.props.componentName,
        metadata: {
          ...this.props.metadata,
          componentStack: errorInfo.componentStack
        }
      },
      'high'
    )
    this.setState({ error })
  }

  render() {
    if (this.state.error) {
      throw this.state.error
    }

    return this.props.children
  }
}

export function withErrorTracking<T extends React.ComponentType<Record<string, unknown>>>(
  Component: T,
  componentName: string
): T {
  const Wrapped = (props: React.ComponentProps<T>) => {
    const metadata = { propKeys: Object.keys(props || {}) }

    return (
      <ErrorTrackingBoundary componentName={componentName} metadata={metadata}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Component {...(props as any)} />
      </ErrorTrackingBoundary>
    )
  }

  Wrapped.displayName = `WithErrorTracking(${Component.displayName || Component.name || 'Component'})`
  return Wrapped as unknown as T
}
